import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * «Deshacer» de 5 segundos en las páginas del cliente (G240, con la regla de G159, ADR-0077): el cambio **no se envía**
 * hasta que pasan los 5 segundos; «Deshacer» simplemente no lo envía. Solo para cambios suyos que no avisan a nadie
 * (nombre, idioma, canal, permisos, aceptar o rechazar una corrección) y la baja de un toque (T37); nunca para borrar,
 * unir cuentas ni pedir la copia.
 *
 * Solo hay uno pendiente a la vez: programar otro envía antes el anterior. Si la página se oculta o se cierra con uno
 * pendiente, se envía en ese momento (era lo que la persona quería).
 */
interface Pendiente {
    clave: string;
    enviar: () => Promise<void>;
    alDeshacer?: () => void;
}

export interface EnvioRetrasado {
    /** Clave del cambio que espera (para enseñar su «Deshacer»), o null. */
    pendiente: string | null;
    programar: (clave: string, enviar: () => Promise<void>, alDeshacer?: () => void) => void;
    deshacer: () => void;
}

export function useEnvioRetrasado(espera = 5000): EnvioRetrasado {
    const [pendiente, setPendiente] = useState<string | null>(null);
    const actual = useRef<Pendiente | null>(null);
    const reloj = useRef<ReturnType<typeof setTimeout> | null>(null);

    const parar = () => {
        if (reloj.current !== null) {
            clearTimeout(reloj.current);
            reloj.current = null;
        }
    };

    /** Envía el pendiente ya (al vencer la espera, al programar otro o al ocultarse la página). */
    const vaciar = useCallback(() => {
        parar();
        const p = actual.current;
        actual.current = null;
        if (p !== null) {
            setPendiente(null);
            void p.enviar();
        }
    }, []);

    const programar = useCallback(
        (clave: string, enviar: () => Promise<void>, alDeshacer?: () => void) => {
            vaciar();
            actual.current = { clave, enviar, alDeshacer };
            setPendiente(clave);
            reloj.current = setTimeout(vaciar, espera);
        },
        [vaciar, espera],
    );

    const deshacer = useCallback(() => {
        parar();
        const p = actual.current;
        actual.current = null;
        setPendiente(null);
        p?.alDeshacer?.();
    }, []);

    useEffect(() => {
        const oculta = () => {
            if (document.visibilityState === 'hidden') {
                vaciar();
            }
        };
        document.addEventListener('visibilitychange', oculta);
        window.addEventListener('pagehide', vaciar);
        return () => {
            document.removeEventListener('visibilitychange', oculta);
            window.removeEventListener('pagehide', vaciar);
            vaciar();
        };
    }, [vaciar]);

    return { pendiente, programar, deshacer };
}
